/** Keep at least this many px of the object box on-stage so it stays selectable. */
export const KEEP_ON_CANVAS_PX = 32;

export const OBJECT_CORNER_HANDLES = new Set([
  "tl",
  "tr",
  "bl",
  "br",
  "nw",
  "ne",
  "sw",
  "se",
  "corner-nw",
  "corner-ne",
  "corner-sw",
  "corner-se",
]);

export const OBJECT_SIDE_HANDLES = new Set([
  "left",
  "right",
  "top",
  "bottom",
  "e",
  "w",
  "n",
  "s",
  "edge-e",
  "edge-w",
  "edge-n",
  "edge-s",
]);

export const OBJECT_RESIZE_HANDLES = [
  "corner-nw",
  "edge-n",
  "corner-ne",
  "edge-e",
  "corner-se",
  "edge-s",
  "corner-sw",
  "edge-w",
];

export function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function clampPositionAllowOverflow(
  x,
  y,
  width,
  height,
  canvasW,
  canvasH,
  keepPx = KEEP_ON_CANVAS_PX
) {
  const minX = keepPx - width;
  const minY = keepPx - height;
  const maxX = canvasW - keepPx;
  const maxY = canvasH - keepPx;
  return {
    x: Math.min(maxX, Math.max(minX, x)),
    y: Math.min(maxY, Math.max(minY, y)),
  };
}

export function normalizeRotationDegrees(deg) {
  const n = ((Number(deg) || 0) % 360) + 360;
  return n % 360;
}

/**
 * Uniform corner scale — opposite corner anchored (CanvasEditor path).
 * @returns {{ size: {width,height}, position: {x,y} }}
 */
export function computeCornerUniformScale({
  handle,
  dx,
  dy,
  initialSize,
  initialPosition,
  canvasSize,
  minSize = 10,
  clampPosition = true,
}) {
  const iw = initialSize.width;
  const ih = initialSize.height;
  const h = handle;

  let sx = 1;
  let sy = 1;
  if (h === "corner-se" || h === "se" || h === "br") {
    sx = (iw + dx) / iw;
    sy = (ih + dy) / ih;
  } else if (h === "corner-sw" || h === "sw" || h === "bl") {
    sx = (iw - dx) / iw;
    sy = (ih + dy) / ih;
  } else if (h === "corner-ne" || h === "ne" || h === "tr") {
    sx = (iw + dx) / iw;
    sy = (ih - dy) / ih;
  } else if (h === "corner-nw" || h === "nw" || h === "tl") {
    sx = (iw - dx) / iw;
    sy = (ih - dy) / ih;
  }

  let scale = (sx + sy) / 2;
  scale = Math.max(scale, minSize / iw, minSize / ih);

  const newWidth = iw * scale;
  const newHeight = ih * scale;
  let newX = initialPosition.x;
  let newY = initialPosition.y;

  if (h === "corner-nw" || h === "nw" || h === "tl") {
    newX = initialPosition.x + (iw - newWidth);
    newY = initialPosition.y + (ih - newHeight);
  } else if (h === "corner-ne" || h === "ne" || h === "tr") {
    newY = initialPosition.y + (ih - newHeight);
  } else if (h === "corner-sw" || h === "sw" || h === "bl") {
    newX = initialPosition.x + (iw - newWidth);
  }

  const position =
    clampPosition && canvasSize?.width > 0
      ? clampPositionAllowOverflow(
          newX,
          newY,
          newWidth,
          newHeight,
          canvasSize.width,
          canvasSize.height
        )
      : { x: newX, y: newY };

  return {
    size: { width: newWidth, height: newHeight },
    position,
  };
}

/**
 * Edge drag crops content (CanvasEditor path). Outward stops at cropExtent.
 * @returns {{ crop, size, position }}
 */
export function computeEdgeCrop({
  handle,
  dx,
  dy,
  initialCrop,
  initialSize,
  initialPosition,
  originalImageSize,
  cropExtent = null,
  canvasSize,
  minDisplay = 10,
  clampPosition = true,
}) {
  const h = handle;
  const scaleX =
    initialCrop.width > 0 ? initialCrop.width / initialSize.width : 1;
  const scaleY =
    initialCrop.height > 0 ? initialCrop.height / initialSize.height : 1;
  const limit =
    cropExtent?.width > 0 && cropExtent?.height > 0
      ? cropExtent
      : {
          x: 0,
          y: 0,
          width: originalImageSize.width,
          height: originalImageSize.height,
        };
  const limitRight = limit.x + limit.width;
  const limitBottom = limit.y + limit.height;

  let newCropX = initialCrop.x;
  let newCropY = initialCrop.y;
  let newCropWidth = initialCrop.width;
  let newCropHeight = initialCrop.height;

  if (h === "left" || h === "w" || h === "edge-w") {
    const leftMaxX = initialCrop.x + initialCrop.width - 1;
    newCropX = clamp(initialCrop.x + dx * scaleX, limit.x, leftMaxX);
    newCropWidth = initialCrop.x + initialCrop.width - newCropX;
  } else if (h === "right" || h === "e" || h === "edge-e") {
    const rightMaxWidth = limitRight - newCropX;
    newCropWidth = clamp(initialCrop.width + dx * scaleX, 1, rightMaxWidth);
  } else if (h === "top" || h === "n" || h === "edge-n") {
    const topMaxY = initialCrop.y + initialCrop.height - 1;
    newCropY = clamp(initialCrop.y + dy * scaleY, limit.y, topMaxY);
    newCropHeight = initialCrop.y + initialCrop.height - newCropY;
  } else if (h === "bottom" || h === "s" || h === "edge-s") {
    const bottomMaxHeight = limitBottom - newCropY;
    newCropHeight = clamp(initialCrop.height + dy * scaleY, 1, bottomMaxHeight);
  }

  const displayScaleX = initialSize.width / initialCrop.width;
  const displayScaleY = initialSize.height / initialCrop.height;
  const newDisplayWidth = Math.max(minDisplay, newCropWidth * displayScaleX);
  const newDisplayHeight = Math.max(minDisplay, newCropHeight * displayScaleY);
  const cropOffsetX = (newCropX - initialCrop.x) * displayScaleX;
  const cropOffsetY = (newCropY - initialCrop.y) * displayScaleY;

  const rawX = initialPosition.x + cropOffsetX;
  const rawY = initialPosition.y + cropOffsetY;
  const position =
    clampPosition && canvasSize?.width > 0
      ? clampPositionAllowOverflow(
          rawX,
          rawY,
          newDisplayWidth,
          newDisplayHeight,
          canvasSize.width,
          canvasSize.height
        )
      : { x: rawX, y: rawY };

  return {
    crop: {
      x: newCropX,
      y: newCropY,
      width: newCropWidth,
      height: newCropHeight,
    },
    size: { width: newDisplayWidth, height: newDisplayHeight },
    position,
  };
}

export function computeRotationFromPointers({
  clientX,
  clientY,
  centerX,
  centerY,
  startAngle,
  initialRotation,
  shiftKey = false,
}) {
  const currentAngle =
    Math.atan2(clientY - centerY, clientX - centerX) * (180 / Math.PI);
  let newRotation = initialRotation + (currentAngle - startAngle);
  if (shiftKey) {
    newRotation = Math.round(newRotation / 45) * 45;
  }
  return normalizeRotationDegrees(newRotation);
}

/**
 * CSS for <img> inside a cropped subject wrapper (CanvasEditor getSubjectImageFrameStyle).
 */
export function subjectImageFrameStyle(crop, originalImageSize) {
  const cropW = crop?.width || 0;
  const cropH = crop?.height || 0;
  const imgW = originalImageSize?.width || 0;
  const imgH = originalImageSize?.height || 0;
  return {
    width: cropW > 0 && imgW > 0 ? `${(imgW / cropW) * 100}%` : "100%",
    height: cropH > 0 && imgH > 0 ? `${(imgH / cropH) * 100}%` : "100%",
    objectFit: "fill",
    maxWidth: "none",
    maxHeight: "none",
    left: cropW > 0 && cropW < imgW ? `${-((crop?.x || 0) / cropW) * 100}%` : "0",
    top: cropH > 0 && cropH < imgH ? `${-((crop?.y || 0) / cropH) * 100}%` : "0",
  };
}

/**
 * inset() clip so the sharp subject stays in the crop box while overflow
 * (ghost) can paint outside during edge-crop (CanvasEditor getImageClipPath).
 */
export function subjectCropClipPath(crop, originalImageSize, objectSize) {
  if (
    !crop?.width ||
    !crop?.height ||
    !originalImageSize?.width ||
    !originalImageSize?.height ||
    !objectSize?.width ||
    !objectSize?.height
  ) {
    return "none";
  }

  const imageScaleX =
    originalImageSize.width > 0
      ? objectSize.width * (originalImageSize.width / crop.width)
      : objectSize.width;
  const imageScaleY =
    originalImageSize.height > 0
      ? objectSize.height * (originalImageSize.height / crop.height)
      : objectSize.height;

  const cropX = (crop.x / originalImageSize.width) * imageScaleX;
  const cropY = (crop.y / originalImageSize.height) * imageScaleY;
  const cropWidth = (crop.width / originalImageSize.width) * imageScaleX;
  const cropHeight = (crop.height / originalImageSize.height) * imageScaleY;

  const insetTop = Math.max(0, cropY);
  const insetLeft = Math.max(0, cropX);
  const insetRight = Math.max(0, imageScaleX - (cropX + cropWidth));
  const insetBottom = Math.max(0, imageScaleY - (cropY + cropHeight));

  return `inset(${insetTop}px ${insetRight}px ${insetBottom}px ${insetLeft}px)`;
}

export function isCornerHandle(handle) {
  return OBJECT_CORNER_HANDLES.has(handle);
}

export function isSideHandle(handle) {
  return OBJECT_SIDE_HANDLES.has(handle);
}
